import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { DAYS, GROUP_ORDER, START_TIMES, END_TIMES, CLOSING_TIMES, DAY_NIGHT_BOUNDARY, UNAVAILABLE} from '../../constants';
import { timeToMinutes as toMinutes, toAmPm } from '../../time';
import { expFor, groupRank } from '../../employees';
import { API_BASE } from './config';
import { useToast } from './Toast';

const buildOptions = (range) => {
    const out = [];
    for (let m = range.begin; m <= range.end; m += 30) {
        const h = Math.floor(m / 60);
        const min = m % 60;
        out.push(`${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`);
    }
    return out;
};

const START_OPTIONS = buildOptions(START_TIMES);
const END_OPTIONS = buildOptions(END_TIMES);



// Include the current value if it falls outside the standard list
const optionsWith = (value, list) =>
    value && !list.includes(value)
        ? [value, ...list]
        : list;

const GeneratedSchedule = ({ schedule, summary, onScheduleUpdate, selectedStore }) => {

    const [editing, setEditing] = useState(null);
    const [closingCell, setClosingCell] = useState(null);
    const [draft, setDraft] = useState({ startTime: '', endTime: '' });
    const [saving, setSaving] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [dragged, setDragged] = useState(null);
    const [dragOverCell, setDragOverCell] = useState(null);
    const copyModeRef = useRef(false);
    const [showingExp, setShowingExp] = useState(null);
    const showToast = useToast();
    const [sortBy, setSortBy] = useState({});
    const [sortMenu, setSortMenu] = useState(null);


    useEffect(() => {
        if (!selectedStore) return;

        const fetchEmployees = async () => {
            try {
                const response = await axios.get(`${API_BASE}/api/employees`, {
                    params: { location: selectedStore }
                });
                setEmployees(response.data);
            } catch (error) {
                console.error('Error fetching employees:', error);
            }
        };
        fetchEmployees();
    }, [selectedStore]);

    const warnings = schedule?.warnings || [];

    // Rebuild the day-keyed shape the summary logic expects
     const byDay = useMemo(() => {
        const out = {};
        DAYS.forEach(day => { out[day] = []; });
        (schedule?.shifts || []).forEach(shift => {
            if (out[shift.day]) out[shift.day].push(shift);
        });
        return out;
    }, [schedule]);

    const salesByDay = useMemo(() => {
        const out = {};
        (schedule?.salesData || []).forEach(s => {
            out[s.day] = { day: s.dayShiftSales, night: s.nightShiftSales };
        });
        return out;
    }, [schedule]);

    const { hoursByName, shiftCountByName } = useMemo(() => {
        const hoursByName = {};
        const shiftCountByName = {};
        (schedule?.shifts || []).forEach(s => {
            hoursByName[s.name] = (hoursByName[s.name] || 0) + s.hours;
            shiftCountByName[s.name] = (shiftCountByName[s.name] || 0) + 1;
        });
        return { hoursByName, shiftCountByName };
    }, [schedule]);

    const { unavailableByCell, employeeByName, windowByCell, availabilityLabel } = useMemo(() => {
        const unavailableByCell = {};
        const employeeByName = {};
        const windowByCell = {};
        const availabilityLabel = {};

        employees.forEach(emp => {
            employeeByName[emp.name] = emp;

            (emp.availability || []).forEach(a => {
                const key = `${emp.name}|${a.day}`;

                if (UNAVAILABLE.includes(a.startTime)) {
                    unavailableByCell[key] = a.startTime;
                    return;
                }
                if (!a.startTime || !a.endTime) return;

                windowByCell[key] = {
                    start: toMinutes(a.startTime),
                    end: toMinutes(a.endTime)
                };
                availabilityLabel[key] = `${a.startTime} - ${a.endTime}`;
            });
        });

        return { unavailableByCell, employeeByName, windowByCell, availabilityLabel };
    }, [employees]);

    // Restrict a time list to the employee's window for that day
    const withinWindow = (list, cellKey) => {
        const w = windowByCell[cellKey];
        if (!w || isNaN(w.start) || isNaN(w.end)) return list;
        return list.filter(t => {
            const m = toMinutes(t);
            return m >= w.start && m <= w.end;
        });
    };

    // Only times later than the selected start
    const afterStart = (list, start) => {
        const s = toMinutes(start);
        if (isNaN(s)) return list;
        return list.filter(t => toMinutes(t) > s);
    };

    const { groupedEmployees, employeeMeta } = useMemo(() => {
        const groupedEmployees = {};
        const employeeMeta = {};

        // Every employee on the roster gets a row, scheduled or not
        employees.forEach(emp => {
            const groupName = emp.group || 'Unassigned';
            if (!groupedEmployees[groupName]) groupedEmployees[groupName] = {};
            if (!groupedEmployees[groupName][emp.name]) groupedEmployees[groupName][emp.name] = {};

            employeeMeta[emp.name] = {
                employee: emp._id,
                group: emp.group,
                hourlyRate: emp.rate
            };
        });

        (schedule?.shifts || []).forEach(shift => {
            const groupName = shift.group || 'Unassigned';
            if (!groupedEmployees[groupName]) groupedEmployees[groupName] = {};
            if (!groupedEmployees[groupName][shift.name]) groupedEmployees[groupName][shift.name] = {};

            groupedEmployees[groupName][shift.name][shift.day] = {
                _id: shift._id,
                startTime: shift.startTime,
                endTime: shift.endTime,
                date: shift.date
            };

            employeeMeta[shift.name] = {
                employee: shift.employee,
                group: shift.group,
                hourlyRate: shift.hourlyRate
            };
        });

        return { groupedEmployees, employeeMeta };
    }, [employees, schedule]);
    
    const sortedGroups = useMemo(
        () => Object.keys(groupedEmployees).sort((a, b) => groupRank(a) - groupRank(b)),
        [groupedEmployees]
    );

    const timeOffCount = (empName, status) => {
        const emp = employeeByName[empName];
        if (!emp) return 0;
        return (emp.availability || []).filter(a => a.startTime === status).length;
    };

    // Row order follows the employee roster unless a sort is chosen for the group
    const orderedNames = (group) => {
        const names = Object.keys(groupedEmployees[group]);
        const mode = sortBy[group] || 'roster';

        return names.sort((a, b) => {
            if (mode === 'name') {
                return a.localeCompare(b);
            }
            if (mode === 'hours') {
                return (hoursByName[b] || 0) - (hoursByName[a] || 0);
            }
            if (mode === 'experience') {
                return expFor(employeeByName[b]) - expFor(employeeByName[a]);
            }
            if (mode === 'shifts') {
                return (shiftCountByName[b] || 0) - (shiftCountByName[a] || 0);
            }
            if (mode === 'requested') {
                return timeOffCount(b, 'Requested Off') - timeOffCount(a, 'Requested Off');
            }
            if (mode === 'unavailable') {
                return timeOffCount(b, 'Not Available') - timeOffCount(a, 'Not Available');
            }
            const orderA = employeeByName[a]?.order ?? 999;
            const orderB = employeeByName[b]?.order ?? 999;
            return orderA - orderB;
        });
    };

    const days = schedule ? DAYS : [];

    const gridColumns = { gridTemplateColumns: `20px 120px repeat(${days.length}, 1fr) 100px` };

    // Open the editor on an existing shift, or on an empty cell
    const startEdit = (empName, day, shift) => {
        const cellKey = `${empName}|${day}`;
        const starts = withinWindow(START_OPTIONS, cellKey);
        const defaultStart = starts[0] || START_OPTIONS[0];
        const ends = afterStart(withinWindow(END_OPTIONS, cellKey), defaultStart);

        setEditing(cellKey);
        setDraft(shift
            ? { startTime: shift.startTime, endTime: shift.endTime }
            : { startTime: defaultStart, endTime: ends[0] || END_OPTIONS[0] }
        );
    };

    const cancelEdit = () => {
        setEditing(null);
        setDraft({ startTime: '', endTime: '' });
    };

    const saveShift = async (empName, day, shift, e) => {
        const anchor = e?.currentTarget;

        // Fire immediately so the toast feels connected to the click
        showToast(shift ? 'Shift updated' : 'Shift added', anchor);
        setSaving(true);
        try {
            let response;

            if (shift) {
                response = await axios.patch(
                    `${API_BASE}/api/schedule/${schedule._id}/shifts/${shift._id}`,
                    draft
                );
            } else {
                const meta = employeeMeta[empName];
                response = await axios.post(
                    `${API_BASE}/api/schedule/${schedule._id}/shifts`,
                    {
                        employee: meta.employee,
                        name: empName,
                        group: meta.group,
                        hourlyRate: meta.hourlyRate,
                        day,
                        startTime: draft.startTime,
                        endTime: draft.endTime
                    }
                );
            }

            onScheduleUpdate(response.data);
            setTimeout(cancelEdit, 500);
            cancelEdit();
        } catch (error) {
            console.error('Error saving shift:', error);
            alert(error.response?.data?.message || 'Error saving shift');
        } finally {
            setSaving(false);
        }
    };

    const deleteShift = async (shiftId, e) => {
        const anchor = e?.currentTarget;

        showToast('Shift deleted', anchor);

        setSaving(true);
        try {
            const response = await axios.delete(
                `${API_BASE}/api/schedule/${schedule._id}/shifts/${shiftId}`
            );
            onScheduleUpdate(response.data);
            setTimeout(cancelEdit, 500);
        } catch (error) {
            console.error('Error deleting shift:', error);
            alert(error.response?.data?.message || 'Error deleting shift');
        } finally {
            setSaving(false);
        }
    };

    // --- Row reordering (react-beautiful-dnd) ---

    const onDragEnd = async (result) => {
        const { source, destination } = result;
        if (!destination) return;
        if (source.droppableId !== destination.droppableId) return;
        if (source.index === destination.index) return;

        const group = source.droppableId;
        const names = orderedNames(group);

        const [moved] = names.splice(source.index, 1);
        names.splice(destination.index, 0, moved);

        // Reassign order within this group, mirroring the employee list
        const updated = employees.map(emp => {
            const idx = names.indexOf(emp.name);
            if (emp.group === group && idx !== -1) {
                return { ...emp, order: idx };
            }
            return emp;
        });

        setEmployees(updated);

        try {
            await axios.put(`${API_BASE}/api/employees/updateOrder`, {
                employees: updated
            });
        } catch (error) {
            console.error('Error updating order:', error);
            alert('Error saving order');
        }
    };

    // --- Shift moving (native HTML5 drag and drop) ---

    const handleShiftDragStart = (e, shift, empName, day) => {
        e.dataTransfer.effectAllowed = 'copyMove';
        e.dataTransfer.setData('text/plain', shift._id);
        setDragged({ shiftId: shift._id, fromName: empName, fromDay: day });
    };

    const handleShiftDragEnd = () => {
        setDragged(null);
        setDragOverCell(null);
    };

    const handleCellDragOver = (e, cellKey) => {
        if (!dragged) return;
        e.preventDefault();
        copyModeRef.current = e.altKey;
        e.dataTransfer.dropEffect = e.altKey ? 'copy' : 'move';
        if (dragOverCell !== cellKey) setDragOverCell(cellKey);
    };

    const handleCellDragLeave = (cellKey) => {
        if (dragOverCell === cellKey) setDragOverCell(null);
    };

    const handleCellDrop = async (e, empName, day) => {
        e.preventDefault();
        setDragOverCell(null);

        if (!dragged) return;

        const isCopy = copyModeRef.current;
        const source = dragged;
        setDragged(null);

        // Same cell, nothing to do
        if (source.fromName === empName && source.fromDay === day) return;

        if (unavailableByCell[`${empName}|${day}`]) {
            alert(`${empName} is not available on ${day}.`);
            return;
        }

        const meta = employeeMeta[empName];
        if (!meta) return;

        setSaving(true);
        try {
            const response = await axios.patch(
                `${API_BASE}/api/schedule/${schedule._id}/shifts/${source.shiftId}/move`,
                {
                    employee: meta.employee,
                    name: empName,
                    group: meta.group,
                    hourlyRate: meta.hourlyRate,
                    day,
                    copy: isCopy
                }
            );
            onScheduleUpdate(response.data);
        } catch (error) {
            console.error('Error moving shift:', error);
            alert(error.response?.data?.message || 'Error moving shift');
        } finally {
            setSaving(false);
        }
    };

    // Calculate shift breakdown by day, splitting shifts that span 4pm
    const getShiftBreakdown = (day) => {

        let dayHours = 0;
        let nightHours = 0;
        let dayCost = 0;
        let nightCost = 0;
        let dayCount = 0;
        let nightCount = 0;

        (byDay[day] || []).forEach(s => {
            const start = toMinutes(s.startTime);
            const end = toMinutes(s.endTime);
            if (isNaN(start) || isNaN(end)) return;

            const beforeBoundary = Math.max(0, Math.min(end, DAY_NIGHT_BOUNDARY) - start) / 60;
            const afterBoundary = Math.max(0, end - Math.max(start, DAY_NIGHT_BOUNDARY)) / 60;

            if (beforeBoundary > 0) {
                dayHours += beforeBoundary;
                dayCost += beforeBoundary * s.hourlyRate;
            }
            if (afterBoundary > 0) {
                nightHours += afterBoundary;
                nightCost += afterBoundary * s.hourlyRate;
            }

            // Count each person once, in whichever part holds more of their shift
            if (beforeBoundary >= afterBoundary) {
                dayCount += 1;
            } else {
                nightCount += 1;
            }
        });

        const daySales = salesByDay[day]?.day || 0;
        const nightSales = salesByDay[day]?.night || 0;

        const dayLaborPct = daySales > 0 ? ((dayCost / daySales) * 100).toFixed(1) : '0.0';
        const nightLaborPct = nightSales > 0 ? ((nightCost / nightSales) * 100).toFixed(1) : '0.0';

        return {
            dayHours, nightHours, dayCost, nightCost,
            dayShifts: dayCount,
            nightShifts: nightCount,
            dayLaborPct,
            nightLaborPct,
            daySales,
            nightSales
        };
    };

    // Weekly totals, derived from the same data as the daily rows
    const weeklySummary = (() => {
        let totalHours = 0;
        let totalCost = 0;
        const percentages = [];

        days.forEach(day => {
            const b = getShiftBreakdown(day);
            const hours = b.dayHours + b.nightHours;
            const cost = b.dayCost + b.nightCost;
            const sales = b.daySales + b.nightSales;

            totalHours += hours;
            totalCost += cost;
            percentages.push(sales > 0 ? (cost / sales) * 100 : 0);
        });

        const average = percentages.length
            ? percentages.reduce((a, b) => a + b, 0) / percentages.length
            : 0;

        return {
            totalLaborHours: totalHours.toFixed(2),
            totalLaborCost: totalCost.toFixed(2),
            averageLaborPercentage: average.toFixed(2)
        };
    })();

    return (
        <div>


            {schedule ? (
                <>
                    <h2>
                        Generated Schedule
                        {schedule.weekStart && (
                            <span>
                                {' for week of '}
                                {new Date(schedule.weekStart).toLocaleDateString('en-US', {
                                    month: 'numeric',
                                    day: 'numeric',
                                    year: 'numeric'
                                })}
                            </span>
                        )}
                    </h2>

                    <div className="schedule-grid">
                        <div className="schedule-header" style={gridColumns}>
                            <div></div>
                            <div>Employee</div>
                            {days.map(day => {
                                const date = byDay[day][0]?.date;
                                const shortDate = date
                                    ? new Date(date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })
                                    : '';
                                return (
                                    <div key={day}>
                                        {day.slice(0, 3)} {shortDate}
                                    </div>
                                );
                            })}
                        <div>Hours</div>
                        </div>

                        <DragDropContext onDragEnd={onDragEnd}>
                            {sortedGroups.map(group => (
                                <React.Fragment key={group}>
                                    <h5
                                        onClick={() => setSortMenu(sortMenu === group ? null : group)}
                                        className="sort"
                                    >
                                        {group}
                                        {sortBy[group] && sortBy[group] !== 'roster' && (
                                            <span className="smaller"> — sort by {sortBy[group]}</span>
                                        )}
                                    </h5>
                                    {sortMenu === group && (
                                        <div className="sort-menu">Sort by: 
                                            {[
                                                ['roster', 'Roster order'],
                                                ['name', 'Name'],
                                                ['hours', 'Hours'],
                                                ['experience', 'Experience'],
                                                ['shifts', 'Shift count'],
                                                ['requested', 'Requested Off'],
                                                ['unavailable', 'Not Available']
                                            ].map(([value, label]) => (
                                                <button className="savebtn"
                                                    key={value}
                                                    onClick={() => {
                                                        setSortBy({ ...sortBy, [group]: value });
                                                        setSortMenu(null);
                                                    }}
                                                >
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    <Droppable droppableId={group}>
                                        {(provided) => (
                                            <div className="employee-grid schedule" {...provided.droppableProps} ref={provided.innerRef}>
                                                {orderedNames(group).map((empName, index) => (
                                                    <Draggable
                                                        key={empName}
                                                        draggableId={`${group}-${empName}`}
                                                        index={index}
                                                    >
                                                        {(provided) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                className="schedule-item"
                                                                style={{ ...gridColumns, ...provided.draggableProps.style }}
                                                            >
                                                                <div className="drag-handle" {...provided.dragHandleProps}></div>
                                                                                                                                <div
                                                                    onClick={() => setShowingExp(showingExp === empName ? null : empName)}
                                                                    style={{ cursor: 'pointer' }}
                                                                >
                                                                    {showingExp === empName ? (
                                                                        <div className="exp-detail">
                                                                            {(() => {
                                                                                const emp = employeeByName[empName];
                                                                                if (!emp) return <div>No record</div>;
                                                                                if (emp.positions && emp.positions.length > 0) {
                                                                                    return emp.positions.map(p => (
                                                                                        <div key={p.position}>
                                                                                            {p.position}: {p.experience}
                                                                                        </div>
                                                                                    ));
                                                                                }
                                                                                return (
                                                                                    <div>
                                                                                        Exp: {emp.exp ?? '—'}
                                                                                    </div>
                                                                                );
                                                                            })()}
                                                                        </div>
                                                                    ) : (
                                                                        empName
                                                                    )}
                                                                </div>
                                                                {days.map(day => {
                                                                    const shift = groupedEmployees[group][empName][day];
                                                                    const cellKey = `${empName}|${day}`;
                                                                    const blockedStatus = unavailableByCell[cellKey];
                                                                    const isDropTarget = dragOverCell === cellKey;
                                                                    return (
                                                                        <div
                                                                            key={day}
                                                                            className={`${isDropTarget ? 'drop-target' : ''} ${!shift && !blockedStatus ? 'unscheduled' : ''}`}
                                                                            onDragOver={(e) => handleCellDragOver(e, cellKey)}
                                                                            onDragLeave={() => handleCellDragLeave(cellKey)}
                                                                            onDrop={(e) => handleCellDrop(e, empName, day)}
                                                                        >
                                                                            {editing === cellKey ? (
                                                                                <div className="shift-edit">
                                                                                      <div className="availability-hint">
                                                                                        Availability: {availabilityLabel[cellKey] || 'No availability set'}
                                                                                    </div>
                                                                                    <select
                                                                                        value={draft.startTime}
                                                                                        onChange={(e) => {
                                                                                            const nextStart = e.target.value;
                                                                                            const valid = afterStart(withinWindow(END_OPTIONS, cellKey), nextStart);
                                                                                            const nextEnd = toMinutes(draft.endTime) > toMinutes(nextStart)
                                                                                                ? draft.endTime
                                                                                                : (valid[0] || draft.endTime);
                                                                                            setDraft({ startTime: nextStart, endTime: nextEnd });
                                                                                        }}
                                                                                    >
                                                                                        {optionsWith(draft.startTime, withinWindow(START_OPTIONS, cellKey)).map(t => (
                                                                                            <option key={t} value={t}>{toAmPm(t)}</option>
                                                                                        ))}
                                                                                    </select>
                                                                                    <select
                                                                                        value={draft.endTime}
                                                                                        onChange={(e) => setDraft({ ...draft, endTime: e.target.value })}
                                                                                    >
                                                                                        {optionsWith(draft.endTime, afterStart(withinWindow(END_OPTIONS, cellKey), draft.startTime)).map(t => (
                                                                                            <option key={t} value={t}>{toAmPm(t)}</option>
                                                                                        ))}
                                                                                    </select>
                                                                                    <div>
                                                                                        <button className="savebtn" onClick={(e) => saveShift(empName, day, shift, e)} disabled={saving}>
                                                                                            {saving ? '...' : 'Save'}
                                                                                        </button>
                                                                                        <button className="cxlbtn" onClick={cancelEdit} disabled={saving}>Cancel</button>
                                                                                        {shift && (
                                                                                            <button onClick={(e) => deleteShift(shift._id, e)} disabled={saving}>Delete</button>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            ) : blockedStatus ? (
                                                                                <div className="smaller time-off">
                                                                                    {blockedStatus}
                                                                                </div>
                                                                            ) : shift ? (
                                                                                <div
                                                                                    className="shift-draggable"
                                                                                    draggable
                                                                                    onDragStart={(e) => handleShiftDragStart(e, shift, empName, day)}
                                                                                    onDragEnd={handleShiftDragEnd}
                                                                                    onClick={() => startEdit(empName, day, shift)}
                                                                                >
                                                                                    {toAmPm(shift.startTime)} - {shift.endTime === CLOSING_TIMES[day] ? 'Close' : toAmPm(shift.endTime)}
                                                                                </div>
                                                                            ) : (
                                                                                <div
                                                                                    onClick={() => startEdit(empName, day, shift)}
                                                                                    style={{ cursor: 'pointer' }}
                                                                                    className="smaller"
                                                                                >
                                                                                    Not Scheduled
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                    );
                                                                })}
                                                                <div className="hours-total">
                                                                    {(hoursByName[empName] || 0).toFixed(1)}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </React.Fragment>
                            ))}
                        </DragDropContext>

                        {/* Summary Rows */}
                        <div className="summary-section">
                            <h3>Summary</h3>
                            <div className="schedule-summary" style={{ ...gridColumns }}>
                                <div className="grid-spacer"></div>
                                <div>DAY</div>
                                {days.map(day => {
                                    const breakdown = getShiftBreakdown(day);
                                    return (
                                        <div key={day}>
                                            <div>Staff: {breakdown.dayShifts}</div>
                                            <div>Hours: {breakdown.dayHours.toFixed(1)}</div>
                                            <div>Cost: ${breakdown.dayCost.toFixed(0)}</div>
                                            <div>Labor %: {breakdown.dayLaborPct}%</div>
                                        </div>
                                    );
                                })}
                            <div className="grid-spacer"></div>
                            </div>
                        
                            <div className="schedule-summary" style={gridColumns}>
                                <div className="grid-spacer"></div>
                                <div>NIGHT</div>
                                {days.map(day => {
                                    const breakdown = getShiftBreakdown(day);
                                    return (
                                        <div key={day}>
                                            <div>Staff: {breakdown.nightShifts}</div>
                                            <div>Hours: {breakdown.nightHours.toFixed(1)}</div>
                                            <div>Cost: ${breakdown.nightCost.toFixed(0)}</div>
                                            <div>Labor %: {breakdown.nightLaborPct}%</div>
                                        </div>
                                    );
                                })}
                                <div className="grid-spacer"></div>
                            </div>

                            <div className="schedule-summary" style={{ ...gridColumns }}>
                                <div className="grid-spacer"></div>
                                <div>DAILY TOTAL</div>
                                {days.map(day => {
                                    const breakdown = getShiftBreakdown(day);
                                    const totalHours = breakdown.dayHours + breakdown.nightHours;
                                    const totalCost = breakdown.dayCost + breakdown.nightCost;
                                    const totalSales = breakdown.daySales + breakdown.nightSales;
                                    const totalLaborPct = totalSales > 0 ? ((totalCost / totalSales) * 100).toFixed(1) : '0.0';

                                    return (
                                        <div key={day}>
                                            <div>Total: {totalHours.toFixed(1)} hrs</div>
                                            <div>Cost: ${totalCost.toFixed(0)}</div>
                                            <div>Labor %: {totalLaborPct}%</div>
                                        </div>
                                    );
                                })}
                                <div className="grid-spacer"></div>
                            </div>
                        </div>

                    {/* Overall Summary */}
                    <div className="summary-section" style={{ marginTop: '20px' }}>
                        <h3>Weekly Summary</h3>
                        <div className="summary-stats">
                            <p><strong>Total Labor Hours:</strong> {weeklySummary.totalLaborHours}</p>
                            <p><strong>Total Labor Cost:</strong> ${weeklySummary.totalLaborCost}</p>
                            <p><strong>Average Labor %:</strong> {weeklySummary.averageLaborPercentage}%</p>
                        </div>
                    </div>
                </div>   
                </>
            ) : (
                <p>Please generate a schedule first.</p>
            )}
            {/* Warnings Section */}
            {warnings.length > 0 && (
                <div className="warnings-section">
                    <h3>Warnings</h3>
                    <ul>
                        {warnings.map((warning, index) => (
                            <li key={index}>{warning}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default GeneratedSchedule;